/* Copyright 2026 Win Solutions. SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FC,
  type PropsWithChildren,
} from "react";

import { widget } from "../widget";

export interface AgendaState {
  absent: boolean;
  current?: string;
  next?: string;
  speaking: boolean;
  finished: boolean;
  mayAct: boolean;
  maySelect?: boolean;
  currentIndex?: number | null;
  limitSec?: number;
  startedAt?: number;
  pausedAt?: number;
  supportsPause?: boolean;
  entries?: {
    slotId?: string;
    userId: string;
    index: number;
    label: string;
    status: "current" | "done" | "upcoming";
  }[];
}
type Operation = "get" | "play" | "pause" | "next" | "select";
interface AgendaValue {
  state: AgendaState | null;
  busy: boolean;
  error: boolean;
  act: (operation: Operation, index?: number, slotId?: string) => Promise<void>;
}
const AgendaContext = createContext<AgendaValue | undefined>(undefined);
export const useAgenda = (): AgendaValue | undefined =>
  useContext(AgendaContext);

export const AgendaProvider: FC<PropsWithChildren> = ({ children }) => {
  const [state, setState] = useState<AgendaState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const alive = useRef(false);
  const generation = useRef(0);
  const acting = useRef(false);
  const ask = useCallback(
    async (
      operation: Operation,
      index?: number,
      slotId?: string,
    ): Promise<void> => {
      if (!widget || (operation === "get" && acting.current)) return;
      const id = ++generation.current;
      try {
        const reply = (await widget.api.transport.send("klava.agenda", {
          operation,
          ...(index !== undefined ? { index } : {}),
          ...(slotId ? { slotId } : {}),
        })) as unknown as AgendaState & { error?: unknown };
        if (reply.error) throw new Error("Agenda request failed");
        if (alive.current && id === generation.current) {
          setState(reply);
          setError(false);
        }
      } catch {
        if (alive.current && id === generation.current) setError(true);
      }
    },
    [],
  );
  const act = useCallback(
    async (
      operation: Operation,
      index?: number,
      slotId?: string,
    ): Promise<void> => {
      if (acting.current) return;
      acting.current = true;
      setBusy(true);
      try {
        await ask(operation, index, slotId);
      } finally {
        acting.current = false;
        if (alive.current) setBusy(false);
      }
    },
    [ask],
  );
  useEffect(() => {
    alive.current = true;
    void ask("get");
    const timer = window.setInterval(() => void ask("get"), 3000);
    return (): void => {
      alive.current = false;
      window.clearInterval(timer);
    };
  }, [ask]);
  return (
    <AgendaContext.Provider value={{ state, busy, error, act }}>
      {children}
    </AgendaContext.Provider>
  );
};
